import React, { useEffect, useRef, useState } from 'react';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentProps,
  OverlayScrollbarsComponentRef,
} from 'overlayscrollbars-react';
import { getMobileOperatingSystem } from 'app/utils/utils';
import { Time } from 'types/general.types';
import { ProfileVisibilityDialog } from 'app/components/Profile/ProfileVisibilityDialog';
import { useLocation } from 'react-router-dom';
import { HEADER_HEIGHT } from 'app/components/HeaderBar/constants';
import {
  As,
  Box,
  Card,
  chakra,
  ChakraComponent,
  Flex,
  forwardRef,
  VStack,
} from '@chakra-ui/react';
import { LoadingState } from 'types/enums';
import { getScrollIsMounted } from 'app/store/Settings/selectors';
import { setScrollIsMounted } from 'app/store/Settings/slice';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { useAppDispatch, useAppSelector } from 'app/store';
import { StorageBanner } from '../Storage/StorageBanner';
import { getStyles } from './styles';

interface ScrollableContentProps {
  children: React.ReactNode;
  bootstrapLoadingState: LoadingState;
  isAuthenticated: boolean;
  lastLogin?: Time;
  isApiKeyLoading: boolean;
  disableScroll?: boolean;
}

export const outsideScrollableContentClassName = 'os-host';
export const scrollableContentClassName = 'os-viewport';
export const popperBoundariesElementId = 'popper-boundaries';

type OverlayType = ChakraComponent<As, OverlayScrollbarsComponentProps>;

const Overlay = chakra(
  forwardRef((props, ref) => (
    <OverlayScrollbarsComponent {...props} ref={ref}>
      {props.children}
    </OverlayScrollbarsComponent>
  ))
) as OverlayType;

export const ScrollableContent: React.FC<ScrollableContentProps> = ({
  children,
  bootstrapLoadingState,
  isAuthenticated,
  lastLogin,
  disableScroll,
}) => {
  const dispatch = useAppDispatch();
  const scrollableContainerRef = useRef<OverlayScrollbarsComponentRef>(null);
  const bootstrapFetched = bootstrapLoadingState === LoadingState.FETCHED;
  const bootstrapError = bootstrapLoadingState === LoadingState.ERROR;
  const { pathname } = useLocation();
  const isMobileDevice = Boolean(getMobileOperatingSystem());
  const scrollMounted = useAppSelector(getScrollIsMounted);
  const playerHeight = useAppSelector(getPlayerHeight);

  const [
    showProfileVisibilityDialog,
    setShowProfileVisibilityDialog,
  ] = useState(true);
  const isFirstSignIn = lastLogin?.timestamp === 0;

  useEffect(() => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef?.current
        ?.osInstance()
        ?.elements()
        .viewport.scrollTo(0, 0);
    }
  }, [pathname]);

  const { overlaySx, cardSx } = getStyles({ isMobileDevice, playerHeight });

  return (
    <Overlay
      sx={overlaySx}
      defer
      events={{
        initialized: () => {
          dispatch(setScrollIsMounted(true));
        },
        destroyed: () => {
          dispatch(setScrollIsMounted(false));
        },
      }}
      ref={scrollableContainerRef}
      options={{
        scrollbars: {
          theme: 'test-class',
        },
      }}
    >
      {(bootstrapFetched || bootstrapError) && (
        <Flex
          height="100%"
          position="relative"
          flexDirection="column"
          overflow={disableScroll ? 'hidden' : undefined}
        >
          {scrollMounted &&
            (isAuthenticated ? (
              <VStack overflowX="clip" w="full" rounded="md">
                <Card sx={cardSx}>
                  <StorageBanner />
                  {children}
                </Card>
              </VStack>
            ) : (
              <Flex
                flexDirection="column"
                width="100%"
                position="relative"
                flex={1}
                flexShrink={0}
                flexBasis={cardSx.flexBasis}
              >
                {children}
              </Flex>
            ))}
          <Box
            position="fixed"
            width="100%"
            height={`calc(100vh - ${HEADER_HEIGHT}px)`}
            top={HEADER_HEIGHT}
            left={0}
            zIndex={-1}
            id={popperBoundariesElementId}
          />
          {isAuthenticated && (
            <ProfileVisibilityDialog
              open={isFirstSignIn && showProfileVisibilityDialog}
              close={() => setShowProfileVisibilityDialog(false)}
            />
          )}
        </Flex>
      )}
    </Overlay>
  );
};
